import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { UniversalIdCreateService } from '../../common/services/universal-id-create.service';
import { UniversalIdDetailsComponent } from '../universal-id-details/universal-id-details.component';
import { AlertBannerComponent } from '../alert-banner/alert-banner.component';
import { OrganizationUserDto } from '../../common/interfaces/organization-user-dto';
import { SsoUserCreationRequest } from '../../common/interfaces/sso-user-creation-request';
import { ToastrService } from 'ngx-toastr';
import { DatePipe, NgForOf, NgIf } from '@angular/common';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UserLink } from '../../common/interfaces/user-link-request';
import { UniversalIdUpdateService } from '../../common/services/universal-id-update.service';
import { SsoUserUpdateRequest } from '../../common/interfaces/sso-user-update-request';
import { SsoUserDetailsDto } from '../../common/interfaces/sso-user-details-dto';
import { EnterpriseLabels } from '../../common/interfaces/enterprise-labels';
import { SingleSignOnServiceService } from '../../common/services/single-sign-on.service';
import { SsoUserAttributeTypeService } from '../../common/services/sso-user-attribute-type.service';
import { SsoUserAttributeTypeDto } from '../../common/interfaces/sso-user-attribute-type-dto';
import { IdentityBrokeringPolicyDto } from '../../common/interfaces/identity-brokering-policy-dto';
import Keycloak from 'keycloak-js';
import { isPasswordAllowed } from '../universal-id-details/universal-id-details.utils';
import { contactSupportMessage } from '../../common/constants/error-messages.constants';
import { getPasswordReusedMessage } from '../../common/utils/sso-user-error.util';

@Component({
    selector: 'lib-universal-id-form',
    standalone: true,
    imports: [
        UniversalIdDetailsComponent,
        AlertBannerComponent,
        NgIf,
        NgForOf
    ],
    providers: [DatePipe],
    templateUrl: './universal-id-form.component.html'
})
export class UniversalIdFormComponent implements OnInit, OnDestroy {
    private universalIdCreateService = inject(UniversalIdCreateService);
    private universalIdUpdateService = inject(UniversalIdUpdateService);
    private toastrService = inject(ToastrService);
    private datePipe = inject(DatePipe);
    private readonly keycloak = inject(Keycloak);
    private singleSignOnService = inject(SingleSignOnServiceService);
    private ssoUserAttributeTypeService = inject(SsoUserAttributeTypeService);

    private static readonly MODMED_ADMIN_ROLE = 'ModMedAdmin';

    @Input() enterpriseId = 0;
    @Input() enterpriseLabels!: EnterpriseLabels;
    @Input() selectedAccounts: OrganizationUserDto[] = [];
    @Input() ssoUserDetails?: SsoUserDetailsDto | null;
    @Input() userLinkObs: Observable<UserLink[]> = new Observable<UserLink[]>();
    @Input() saveUniversalIdObs: Observable<boolean> = new Observable<boolean>();
    @Input() isCreateModeUniversalIdFlow = false;
    @Input() triggerAutopopulate = false;

    @Output() submitted = new EventEmitter<boolean>();
    @Output() formValidation = new EventEmitter<{ valid: boolean, dirty: boolean }>();

    private userLinkSubscription = new Subscription();
    private saveUniversalIdSubscription = new Subscription();

    isCreateMode = true;
    universalIdDetailsValid = false;
    errorMessages: string[] = [];
    userLinks: UserLink[] = [];
    formData: Partial<SsoUserCreationRequest> = {};
    alertMessages: string[] = [];
    idpAttributes: SsoUserAttributeTypeDto[] = [];
    identityBrokeringPolicy: IdentityBrokeringPolicyDto | null = null;

    ngOnInit(): void {
        this.setCreateMode();
        this.userLinkSubscription = this.userLinkObs.subscribe((userLinks: UserLink[]) => this.userLinks = userLinks);
        this.saveUniversalIdSubscription = this.saveUniversalIdObs.subscribe(() => this.isCreateMode ? this.create() : this.update());
        this.setAlertMessage();
        this.loadIdpAttributes();
    }

    ngOnDestroy(): void {
        this.userLinkSubscription.unsubscribe();
        this.saveUniversalIdSubscription.unsubscribe();
    }

    universalIdDetailsFormUpdated(event: { value: Partial<SsoUserCreationRequest>, valid: boolean, dirty?: boolean }): void {
        this.formData = event.value;
        this.universalIdDetailsValid = event.valid;
        this.formValidation.emit({
            valid: this.universalIdDetailsValid,
            dirty: event.dirty ?? false
        });
    }

    checkValidForms(): boolean {
        return this.universalIdDetailsValid;
    }

    setCreateMode(): void {
        this.isCreateMode = !this.ssoUserDetails || !this.ssoUserDetails.id?.length;
    }

    populateUserLinks(): void {
        if (this.selectedAccounts.length > 0) {
            for (const account of this.selectedAccounts) {
                this.userLinks.push({
                    systemId: 'EMA',
                    organizationId: account.organizationId,
                    userId: String(account.userId),
                    primary: false
                });
            }
        }
    }

    create(): void {
        this.errorMessages = [];
        if (this.checkValidForms()) {
            this.universalIdCreateService.createUniversalId(this.populateSsoUserCreateRequest()).subscribe({
                next: () => {
                    this.toastrService.success('The information was successfully saved', this.enterpriseLabels.universalIdLabel);
                    this.submitted.emit(true);
                },
                error: (error: HttpErrorResponse) => this.handleErrors(error)
            });
        }
    }

    update(): void {
        this.errorMessages = [];
        if (this.checkValidForms()) {
            this.universalIdUpdateService.updateUniversalId(this.populateSsoUserUpdateRequest()).subscribe({
                next: () => {
                    this.toastrService.success('The information was successfully updated',  this.enterpriseLabels.universalIdLabel);
                    this.submitted.emit(true);
                },
                error: (error: HttpErrorResponse) => this.handleErrors(error)
            });
        }
    }

    hasError(error: HttpErrorResponse, message: string) {
        return error.error?.message?.includes(message)
            || error.error?.messages?.findIndex((e: string) => e.includes(message)) > -1;
    }

    private handleErrors(error: HttpErrorResponse): void {
        const action = this.isCreateMode ? 'create' : 'update';

        this.errorMessages = [];

        const passwordReusedMessage = getPasswordReusedMessage(error);
        if (passwordReusedMessage) {
            this.errorMessages.push(passwordReusedMessage);
        }

        if (error.status === HttpStatusCode.BadRequest) {
            if (this.hasError(error, 'Username is not unique')) {
                this.errorMessages.push(
                    `Cannot ${action} the ${this.enterpriseLabels.universalIdLabel}. Inserted username is already associated to another ${this.enterpriseLabels.universalIdLabel}.`
                );
            }

            if (this.hasError(error, 'Email address not unique in Enterprise')) {
                this.errorMessages.push(
                    `Cannot ${action} the ${this.enterpriseLabels.universalIdLabel}. The email address is linked to another ${this.enterpriseLabels.universalIdLabel}.`
                );
            }

            if (this.hasError(error, 'Required IDP attribute is missing')) {
                this.errorMessages.push(
                    `Cannot ${action} the ${this.enterpriseLabels.universalIdLabel}. An IDP attribute value is missing.`
                );
            }
        }

        if (error.status !== HttpStatusCode.Ok && this.errorMessages.length === 0) {
            this.errorMessages.push(`Failed to ${action} the ${this.enterpriseLabels.universalIdLabel}. ${contactSupportMessage}`);
        }

        if (
            error.status === HttpStatusCode.InternalServerError &&
            this.hasError(error, 'uidx_user_sso_link__organization_user_id')
        ) {
            this.toastrService.error(
                `The ${this.enterpriseLabels.universalIdLabel} was ${action}d but one or more ${this.enterpriseLabels.accountLabel}
                 accounts could not be linked because they are linked to an existing ${this.enterpriseLabels.universalIdLabel}.`,
                ''
            );
            this.submitted.emit(true);
        }
    }

    private populateSsoUserCreateRequest(): SsoUserCreationRequest {
        const shouldApplyEnterpriseAdmin = this.formData.enterpriseAdmin ?? false;
        const isModMedAdmin = this.keycloak.hasRealmRole(UniversalIdFormComponent.MODMED_ADMIN_ROLE);
        const ssoUserCreationRequest: SsoUserCreationRequest = {
            enterpriseId: this.enterpriseId,
            username: this.formData.username ?? '',
            firstName: this.formData.firstName ?? '',
            lastName: this.formData.lastName ?? '',
            dateOfBirth: this.datePipe.transform(this.formData.dateOfBirth, 'MM/dd/yyyy') ?? '',
            email: this.formData.email ?? '',
            phone: this.formData.phone ?? '',
            enterpriseAdmin: shouldApplyEnterpriseAdmin,
            enterprisePrimaryAdmin: isModMedAdmin && shouldApplyEnterpriseAdmin,
            autoVerifyEmail: isModMedAdmin
        };

        if (this.formData.password && this.formData.password.trim() !== '') {
            ssoUserCreationRequest.password = this.formData.password;
        }

        this.populateUserLinks();

        if (this.userLinks.length > 0) {
            ssoUserCreationRequest.userLinks = this.userLinks;
        }

        if (this.formData.ssoUserAttributes && this.formData.ssoUserAttributes.length > 0) {
            ssoUserCreationRequest.ssoUserAttributes = this.formData.ssoUserAttributes;
        }

        return ssoUserCreationRequest;
    }

    private populateSsoUserUpdateRequest(): SsoUserUpdateRequest {
        const ssoUserUpdateRequest: SsoUserUpdateRequest = {
            enterpriseId: this.enterpriseId,
            userId: this.ssoUserDetails?.id ?? '',
            username: this.formData.username,
            firstName: this.formData.firstName,
            lastName: this.formData.lastName,
            dateOfBirth: this.datePipe.transform(this.formData.dateOfBirth, 'MM/dd/yyyy') ?? '',
            email: this.formData.email,
            phone: this.formData.phone,
            unlock: this.formData.unlock,
            enterpriseAdmin: this.formData.enterpriseAdmin
        };

        if (this.formData.unlock) {
            ssoUserUpdateRequest.enabled = true;
        }

        if (this.formData.password && this.formData.password.trim() !== '') {
            ssoUserUpdateRequest.password = this.formData.password;
        }

        this.populateUserLinks();

        if (this.userLinks.length > 0) {
            ssoUserUpdateRequest.userLinks = this.userLinks;
        }

        if (this.formData.ssoUserAttributes && this.formData.ssoUserAttributes.length > 0) {
            ssoUserUpdateRequest.ssoUserAttributes = this.formData.ssoUserAttributes;
        }

        return ssoUserUpdateRequest;
    }

    private setAlertMessage(): void {
        if (this.isCreateModeUniversalIdFlow) {
            this.alertMessages.push(`The first ${this.enterpriseLabels.accountLabel} linked will auto-populate profile data.`);
        }
    }

    private loadIdpAttributes(): void {
        forkJoin({
            policy: this.singleSignOnService.getIdentityBrokeringPolicy(this.enterpriseId).pipe(
                catchError(() => of(null))
            ),
            allAttributes: this.ssoUserAttributeTypeService.getIdpAttributes().pipe(
                catchError(() => of([]))
            )
        }).subscribe(({ policy, allAttributes }) => {
            this.identityBrokeringPolicy = policy;
            this.filterIdpAttributes(policy, allAttributes);
            if (this.isCreateMode && isPasswordAllowed(policy)) {
                this.alertMessages.unshift('User will be required to reset their password on next login.');
            }
        });
    }

    private filterIdpAttributes(policy: IdentityBrokeringPolicyDto | null, allAttributes: SsoUserAttributeTypeDto[]): void {
        if (policy?.attributeMappings && allAttributes.length > 0) {
            const configuredAttributeNames = Object.keys(policy.attributeMappings);
            this.idpAttributes = allAttributes.filter(
                attr => configuredAttributeNames.includes(attr.name) && attr.editable
            );
        }
    }
}
